import React, { useEffect, useState } from "react";
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress,
  Divider, List, ListItem, ListItemText, Alert, LinearProgress, Tooltip
} from "@mui/material";
import {
  TrendingUp, Lightbulb, Category, Psychology, AutoAwesome, BarChart
} from "@mui/icons-material";
import { collection, query, orderBy, limit, getDocs } from "firebase/firestore";
import { db } from "../firebase";

interface CategoryBreakdown {
  [key: string]: number;
}

interface TopIssue {
  issue: string;
  count: number;
  category: string;
}

interface SuggestedCategory {
  name: string;
  description: string;
  exampleRequests: string[];
}

interface Insight {
  id: string;
  totalAnalyzed: number;
  categoryBreakdown: CategoryBreakdown;
  topIssues: TopIssue[];
  emergingPatterns: string[];
  suggestedNewCategories: SuggestedCategory[];
  qualityInsights: string[];
  summary: string;
  analyzedAt: any;
  jobsAnalyzed: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  wifi_networking: "Wi-Fi & Networking",
  computer_repair: "Computer Repair",
  smart_home: "Smart Home",
  phone_tablet: "Phone & Tablet",
  tv_setup: "TV Setup",
  printer_setup: "Printer Setup",
  social_media: "Social Media",
  virus_removal: "Virus Removal",
  uncategorized: "Uncategorized",
};

const CATEGORY_COLORS: Record<string, string> = {
  wifi_networking: "#663399",
  computer_repair: "#2196F3",
  smart_home: "#4CAF50",
  phone_tablet: "#FF9800",
  tv_setup: "#E91E63",
  printer_setup: "#795548",
  social_media: "#03A9F4",
  virus_removal: "#F44336",
  uncategorized: "#9E9E9E",
};

export default function InsightsView() {
  const [insights, setInsights] = useState<Insight[]>([]);
  const [loading, setLoading] = useState(true);
  const latest = insights[0];

  useEffect(() => {
    const load = async () => {
      try {
        const q = query(
          collection(db, "issueInsights"),
          orderBy("analyzedAt", "desc"),
          limit(10)
        );
        const snap = await getDocs(q);
        setInsights(snap.docs.map(d => ({ id: d.id, ...d.data() } as Insight)));
      } catch (e) {
        // Collection doesn't exist yet — show empty state
        setInsights([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return (
    <Box sx={{ display: "flex", justifyContent: "center", pt: 8 }}>
      <CircularProgress sx={{ color: "#663399" }} />
    </Box>
  );

  if (!latest) return (
    <Box sx={{ p: 4 }}>
      <Alert severity="info" sx={{ borderRadius: 2 }}>
        <Typography fontWeight={700}>No insights yet</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Insights are generated nightly at 2 AM from emergency job descriptions.
          Come back tomorrow after your first day of real bookings!
        </Typography>
      </Alert>
      <Box sx={{ mt: 3, p: 3, bgcolor: "rgba(102,51,153,0.06)", borderRadius: 2 }}>
        <Typography variant="body2" color="text.secondary">
          💡 <strong>How it works:</strong> Every emergency request customers submit is analyzed
          by Gemini AI to identify patterns, emerging service needs, and categories you might
          be missing. The more bookings you get, the smarter it becomes.
        </Typography>
      </Box>
    </Box>
  );

  // Calculate total for percentages
  const total = Object.values(latest.categoryBreakdown).reduce((a, b) => a + b, 0);

  return (
    <Box sx={{ p: 3, maxWidth: 1100, mx: "auto" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={800} sx={{ color: "#663399" }}>
          🧠 AI Issue Insights
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Powered by Gemini · Updated nightly · Based on {latest.jobsAnalyzed} recent requests
          {latest.analyzedAt?.toDate && (
            <> · Last run: {latest.analyzedAt.toDate().toLocaleDateString("en-US", {
              weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
            })}</>
          )}
        </Typography>
      </Box>

      {/* Summary */}
      <Card sx={{ mb: 3, borderLeft: "4px solid #663399", borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            <Psychology sx={{ color: "#663399" }} />
            <Typography fontWeight={700}>AI Summary</Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {latest.summary}
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
        {/* Category Breakdown */}
        <Box sx={{ flex: "1 1 300px", minWidth: 0 }}>
          <Card sx={{ borderRadius: 2, height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <BarChart sx={{ color: "#663399" }} />
                <Typography fontWeight={700}>Category Breakdown</Typography>
              </Box>
              {Object.entries(latest.categoryBreakdown)
                .sort(([, a], [, b]) => b - a)
                .filter(([, count]) => count > 0)
                .map(([cat, count]) => {
                  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <Box key={cat} sx={{ mb: 1.5 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {CATEGORY_LABELS[cat] ?? cat}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {count} ({pct}%)
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          bgcolor: "rgba(0,0,0,0.08)",
                          "& .MuiLinearProgress-bar": {
                            bgcolor: CATEGORY_COLORS[cat] ?? "#663399",
                            borderRadius: 3,
                          }
                        }}
                      />
                    </Box>
                  );
                })}
            </CardContent>
          </Card>
        </Box>

        {/* Top Issues */}
        <Box sx={{ flex: "1 1 300px", minWidth: 0 }}>
          <Card sx={{ borderRadius: 2, height: "100%" }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <TrendingUp sx={{ color: "#663399" }} />
                <Typography fontWeight={700}>Top Issues</Typography>
              </Box>
              {latest.topIssues?.length > 0 ? (
                <List dense disablePadding>
                  {latest.topIssues.slice(0, 8).map((issue, i) => (
                    <React.Fragment key={i}>
                      <ListItem disablePadding sx={{ py: 0.75 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  bgcolor: "rgba(102,51,153,0.1)",
                                  color: "#663399",
                                  px: 0.75, py: 0.25,
                                  borderRadius: 1,
                                  fontWeight: 700,
                                  minWidth: 20,
                                  textAlign: "center"
                                }}
                              >
                                {issue.count}
                              </Typography>
                              <Typography variant="body2">{issue.issue}</Typography>
                            </Box>
                          }
                          secondary={
                            <Chip
                              label={CATEGORY_LABELS[issue.category] ?? issue.category}
                              size="small"
                              sx={{
                                mt: 0.25,
                                height: 18,
                                fontSize: 10,
                                bgcolor: `${CATEGORY_COLORS[issue.category] ?? "#663399"}20`,
                                color: CATEGORY_COLORS[issue.category] ?? "#663399",
                              }}
                            />
                          }
                        />
                      </ListItem>
                      {i < latest.topIssues.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">No top issues identified yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Emerging Patterns */}
        <Box sx={{ flex: "1 1 300px", minWidth: 0 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <AutoAwesome sx={{ color: "#FF8C00" }} />
                <Typography fontWeight={700}>Emerging Patterns</Typography>
              </Box>
              {latest.emergingPatterns?.length > 0 ? (
                <List dense disablePadding>
                  {latest.emergingPatterns.map((pattern, i) => (
                    <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Typography color="#FF8C00" sx={{ mt: 0.1 }}>→</Typography>
                            <Typography variant="body2">{pattern}</Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">No patterns detected yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Quality Insights */}
        <Box sx={{ flex: "1 1 300px", minWidth: 0 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Lightbulb sx={{ color: "#4CAF50" }} />
                <Typography fontWeight={700}>Actionable Insights</Typography>
              </Box>
              {latest.qualityInsights?.length > 0 ? (
                <List dense disablePadding>
                  {latest.qualityInsights.map((insight, i) => (
                    <ListItem key={i} disablePadding sx={{ py: 0.5 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Typography color="#4CAF50" sx={{ mt: 0.1 }}>✓</Typography>
                            <Typography variant="body2">{insight}</Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">No insights yet.</Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Suggested New Categories */}
        {latest.suggestedNewCategories?.length > 0 && (
          <Box sx={{ width: "100%" }}>
            <Card sx={{ borderRadius: 2, borderLeft: "4px solid #FF8C00" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <Category sx={{ color: "#FF8C00" }} />
                  <Typography fontWeight={700}>Suggested New Categories</Typography>
                  <Chip
                    label="Action Required"
                    size="small"
                    sx={{ bgcolor: "rgba(255,140,0,0.15)", color: "#FF8C00", fontWeight: 700 }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Gemini identified these service types in customer requests that don't fit existing categories.
                  Consider adding them to the app.
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {latest.suggestedNewCategories.map((cat, i) => (
                    <Box key={i} sx={{ flex: "1 1 240px", minWidth: 0 }}>
                      <Box sx={{
                        p: 2,
                        bgcolor: "rgba(255,140,0,0.06)",
                        borderRadius: 2,
                        border: "1px solid rgba(255,140,0,0.2)"
                      }}>
                        <Typography fontWeight={700} sx={{ mb: 0.5 }}>{cat.name}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {cat.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          Example requests:
                        </Typography>
                        {cat.exampleRequests?.map((ex, j) => (
                          <Typography key={j} variant="caption" display="block" color="text.secondary" sx={{ pl: 1 }}>
                            • "{ex}"
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Historical trend */}
        {insights.length > 1 && (
          <Box sx={{ width: "100%" }}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Typography fontWeight={700} sx={{ mb: 2 }}>📈 Historical Analysis Runs</Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {insights.map((ins, i) => (
                    <Tooltip
                      key={ins.id}
                      title={`${ins.jobsAnalyzed} requests analyzed`}
                    >
                      <Chip
                        label={ins.analyzedAt?.toDate
                          ? ins.analyzedAt.toDate().toLocaleDateString("en-US", { month: "short", day: "numeric" })
                          : `Run ${insights.length - i}`
                        }
                        size="small"
                        sx={{
                          bgcolor: i === 0 ? "rgba(102,51,153,0.15)" : "rgba(0,0,0,0.06)",
                          color: i === 0 ? "#663399" : "text.secondary",
                          fontWeight: i === 0 ? 700 : 400,
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
}
